if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // --- ADD THIS BLOCK TO FIX THE INITIAL LOAD ---
        // We wait a tiny bit to ensure all media elements are in the DOM
        setTimeout(() => {
          const initialVariant = this.getSelectedVariant(this);
          if (initialVariant) {
            this.updateMedia(document, initialVariant.featured_media?.id);
          }
        }, 100);
        // --- END INITIAL LOAD FIX ---

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, target, selectedOptionValues),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, target, selectedOptionValues) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          // Get the option position from the target element
          if (selectedOptionValues && selectedOptionValues.length > 0) {
            if (target && target.dataset && target.dataset.optionPosition) {
              const optionPosition = target.dataset.optionPosition;
              const optionValue = target.value;
            }
          }

          // If variant is unavailable, find next available variant
          if (variant && !variant.available) {
            const productData = this.querySelector('script[data-product]');
            if (productData) {
              try {
                const product = JSON.parse(productData.textContent);
                
                // Find next available variant that preserves user's selection
                const allAvailable = product.variants.filter(v => v.available);
                
                if (target && target.dataset && target.dataset.optionPosition) {
                  const optionPosition = parseInt(target.dataset.optionPosition);
                  
                  let nextAvailableVariants = [];
                  const variantSelects = this.querySelector('variant-selects');
                  let actualOptionValues = [];
                  
                  if (variantSelects) {
                    const selectedInputs = variantSelects.querySelectorAll('input:checked, select');
                    selectedInputs.forEach((input, index) => {
                      actualOptionValues[index] = input.value;
                    });
                  }
                  
                  if (optionPosition === 1) {
                    nextAvailableVariants = allAvailable.filter(v => v.option1 === actualOptionValues[0]);
                  } else if (optionPosition === 2) {
                    nextAvailableVariants = allAvailable.filter(v => v.option2 === actualOptionValues[1]);
                  } else if (optionPosition === 3) {
                    nextAvailableVariants = allAvailable.filter(v => v.option3 === actualOptionValues[2]);
                  }
                  
                  if (nextAvailableVariants.length > 0) {
                    const nextVariant = nextAvailableVariants[0];
                    const targetOptionPosition = optionPosition === 1 ? 2 : 1;
                    this.autoSelectNextAvailableOption(nextVariant, targetOptionPosition);
                  }
                }
                
              } catch (e) {
                console.error(e);
              }
            }
          }
          
          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        // 1. Capture the selected color from the variant selects
        const variantSelects = this.querySelector('variant-selects');
        const selectedInput = variantSelects?.querySelector('input:checked') || variantSelects?.querySelector('select');
        const selectedColor = selectedInput ? selectedInput.value : null;

        // 2. Create Regex to find "color: ColorName" in Alt tags
        const colorRegex = selectedColor ? new RegExp(`color:\\s*${selectedColor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i') : null;

        if (mediaGallerySource && mediaGalleryDestination) {
          // 3. Filter items: show if it matches the color OR has no "color:" tag at all
          const filteredItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'))
            .filter(item => {
              const mediaAlt = item.querySelector('img')?.getAttribute('alt') || '';
              if (mediaAlt.toLowerCase().includes('color:')) {
                return colorRegex && colorRegex.test(mediaAlt);
              }
              return true; // Show general images
            });

          // 4. Sort: Color-specific images first, general images last
          filteredItems.sort((a, b) => {
            const altA = (a.querySelector('img')?.getAttribute('alt') || '').toLowerCase();
            const altB = (b.querySelector('img')?.getAttribute('alt') || '').toLowerCase();
            const aHasColor = altA.includes('color:');
            const bHasColor = altB.includes('color:');
            return aHasColor && !bHasColor ? -1 : (!aHasColor && bHasColor ? 1 : 0);
          });

          // 5. Clear and rebuild with De-duplication
          mediaGallerySource.innerHTML = ''; 
          const seenMediaIds = new Set(); 

          filteredItems.forEach(item => {
            const mediaId = item.dataset.mediaId;
            if (!seenMediaIds.has(mediaId)) {
              mediaGallerySource.appendChild(item.cloneNode(true));
              seenMediaIds.add(mediaId);
            }
          });
        }

        // 6. Re-trigger theme gallery components
        const gallery = this.querySelector(`media-gallery`);
        if (gallery) {
            gallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
            
            const slider = gallery.querySelector('slider-component');
            if (slider && typeof slider.initPages === 'function') {
                slider.initPages();
            }
        }

        // 7. Update Modal Content
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.updateDiscountUpsellWidget(html)
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      updateDiscountUpsellWidget(html) {
        if (!!this.querySelector('.js-pdp-discount-upsells')) {
          this.querySelector('.js-pdp-discount-upsells').innerHTML = html.querySelector('.js-pdp-discount-upsells').innerHTML
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      // Auto-select the next available option by finding and clicking the input
      autoSelectNextAvailableOption(nextVariant, optionPosition) {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;
        
        const fieldsets = variantSelects.querySelectorAll('fieldset');
        const targetFieldset = fieldsets[optionPosition - 1];
        
        if (!targetFieldset) return;
        
        let targetOptionValue;
        if (optionPosition === 1) {
          targetOptionValue = nextVariant.option1;
        } else if (optionPosition === 2) {
          targetOptionValue = nextVariant.option2;
        } else if (optionPosition === 3) {
          targetOptionValue = nextVariant.option3;
        }
        
        if (!targetOptionValue) return;
        
        const targetInput = targetFieldset.querySelector(`input[value="${targetOptionValue}"]`);
        
        if (targetInput) {
          targetInput.click();
        }
      }
    }
  );
}

